import type { Coordinate, Navigation } from "../types";

export interface RouteElevationSample {
  coord: Coordinate;
  distanceKm: number;
}

export function sampleRoutePath(path: Coordinate[], maxSamples = 100, targetSpacingKm = 1): RouteElevationSample[] {
  const clean = path.filter(([latitude, longitude]) => Number.isFinite(latitude) && Number.isFinite(longitude));
  if (clean.length < 2) return [];
  const cumulativeMeters = [0];
  for (let index = 1; index < clean.length; index++) {
    cumulativeMeters.push(cumulativeMeters[index - 1] + distanceMeters(clean[index - 1], clean[index]));
  }
  const totalMeters = cumulativeMeters.at(-1) ?? 0;
  if (totalMeters <= 0) return [{ coord: clean[0], distanceKm: 0 }, { coord: clean.at(-1)!, distanceKm: 0 }];
  const count = Math.min(maxSamples, Math.max(2, Math.ceil(totalMeters / (targetSpacingKm * 1000)) + 1));
  const samples: RouteElevationSample[] = [];
  let segmentIndex = 1;
  for (let index = 0; index < count; index++) {
    const targetMeters = index === count - 1 ? totalMeters : totalMeters * index / (count - 1);
    while (segmentIndex < cumulativeMeters.length - 1 && cumulativeMeters[segmentIndex] < targetMeters) segmentIndex++;
    const startDistance = cumulativeMeters[segmentIndex - 1];
    const endDistance = cumulativeMeters[segmentIndex];
    const ratio = endDistance === startDistance ? 0 : (targetMeters - startDistance) / (endDistance - startDistance);
    const start = clean[segmentIndex - 1];
    const end = clean[segmentIndex];
    samples.push({ coord: [start[0] + (end[0] - start[0]) * ratio, start[1] + (end[1] - start[1]) * ratio], distanceKm: targetMeters / 1000 });
  }
  return samples;
}

export function buildElevationPeaks(profile: NonNullable<Navigation["profile"]>): NonNullable<Navigation["peaks"]> {
  if (profile.length < 3) return [];
  return profile.flatMap((point, index) => {
    if (!index || index === profile.length - 1) return [];
    const prominence = point.elevation - Math.max(profile[index - 1].elevation, profile[index + 1].elevation);
    return prominence >= 20 ? [{ ...point }] : [];
  }).sort((left, right) => right.elevation - left.elevation).slice(0, 8).sort((left, right) => left.distanceKm - right.distanceKm);
}

function distanceMeters(left: Coordinate, right: Coordinate) {
  const radians = Math.PI / 180;
  const lat1 = left[0] * radians;
  const lat2 = right[0] * radians;
  const deltaLat = (right[0] - left[0]) * radians;
  const deltaLng = (right[1] - left[1]) * radians;
  const value = Math.sin(deltaLat / 2) ** 2 + Math.cos(lat1) * Math.cos(lat2) * Math.sin(deltaLng / 2) ** 2;
  return 6371000 * 2 * Math.atan2(Math.sqrt(value), Math.sqrt(1 - value));
}
